import React, { createContext, useContext, useMemo } from 'react';

/**
 * RouteConfigurationContext holds the resolved array of route objects.
 * We make the provider defensive so it can accept either:
 *  - an array of routes (already resolved)
 *  - a function that returns the array (routeConfiguration function)
 *  - something falsy (will resolve to empty array)
 *
 * This prevents accidental runtime errors where a function/object is passed
 * into React where an array of routes is expected.
 */

const RouteConfigurationContext = createContext([]);

/**
 * RouteConfigurationProvider - resolves the incoming value to an array
 * and provides it via context.
 *
 * Usage:
 * <RouteConfigurationProvider value={routeConfigOrFn}>
 *   {children}
 * </RouteConfigurationProvider>
 */
export const RouteConfigurationProvider = ({ value, children }) => {
  const resolved = useMemo(() => {
    try {
      if (Array.isArray(value)) return value;
      if (typeof value === 'function') {
        // If the function expects layout/accessControl args, callers should
        // pass the resolved result. Here we call without args to cover the
        // common case where value is a zero-arg function returning routes.
        const maybe = value();
        return Array.isArray(maybe) ? maybe : (maybe || []);
      }
      // If it's an object already (unlikely), try to coerce to array safely.
      if (value && typeof value === 'object' && value.routes && Array.isArray(value.routes)) {
        return value.routes;
      }
      return Array.isArray(value) ? value : [];
    } catch (e) {
      // Defensive: if something throws, return empty array instead of crashing.
      // eslint-disable-next-line no-console
      console.warn('Failed to resolve routeConfiguration value in provider:', e);
      return [];
    }
  }, [value]);

  return (
    <RouteConfigurationContext.Provider value={resolved}>
      {children}
    </RouteConfigurationContext.Provider>
  );
};

export const useRouteConfiguration = () => useContext(RouteConfigurationContext);

export default RouteConfigurationContext;
